import React, { useEffect, useState, useCallback } from "react";
import { MagnifyingGlass, CaretLeft, CaretRight, Crown } from "@phosphor-icons/react";
import api from "@/lib/api";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function AdminUsers() {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/users?search=${encodeURIComponent(query)}&page=${page}&limit=20`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [query, page]);

  useEffect(() => { load(); }, [load]);

  const submitSearch = (e) => { e.preventDefault(); setPage(1); setQuery(search); };

  return (
    <div data-testid="admin-users">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Users</p>
      <h1 className="mt-2 font-heading text-3xl font-light tracking-tight text-slate-50">User directory</h1>
      <p className="mt-2 text-sm text-slate-400">{data ? `${data.total.toLocaleString()} total users` : "Loading…"}</p>

      <form onSubmit={submitSearch} className="mt-6 flex max-w-md gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email or name…" className="w-full rounded-full border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-4 text-sm text-slate-200 placeholder-slate-600 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30" data-testid="admin-users-search" />
        </div>
        <button type="submit" className="rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-amber-400" data-testid="admin-users-search-btn">Search</button>
      </form>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-800">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-900 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3 font-medium">User</th>
                <th className="px-5 py-3 font-medium">Joined</th>
                <th className="px-5 py-3 font-medium text-center">Assessments</th>
                <th className="px-5 py-3 font-medium text-center">Journals</th>
                <th className="px-5 py-3 font-medium text-center">Coach msgs</th>
                <th className="px-5 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 bg-slate-950">
              {loading ? (
                <tr><td colSpan={6} className="px-5 py-16 text-center"><div className="mx-auto h-7 w-7 animate-spin rounded-full border-2 border-slate-800 border-t-amber-400" /></td></tr>
              ) : !data || data.users.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-16 text-center text-slate-500">No users found.</td></tr>
              ) : (
                data.users.map((u) => (
                  <tr key={u.user_id} className="transition-colors hover:bg-slate-900/50" data-testid={`admin-user-row-${u.user_id}`}>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-slate-100">{u.name || "—"}</span>
                        {u.role === "admin" && <Crown size={13} weight="fill" className="text-amber-400" title="Admin" />}
                      </div>
                      <span className="text-xs text-slate-500">{u.email}</span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-400">{fmtDate(u.created_at)}</td>
                    <td className="px-5 py-3.5 text-center font-mono text-slate-200">{u.assessments}</td>
                    <td className="px-5 py-3.5 text-center font-mono text-slate-200">{u.journals}</td>
                    <td className="px-5 py-3.5 text-center font-mono text-slate-200">{u.coach_messages}</td>
                    <td className="px-5 py-3.5">
                      {u.role === "admin" ? <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-xs text-amber-300">Admin</span>
                        : u.is_lead ? <span className="rounded-full bg-slate-800 px-2.5 py-1 text-xs text-slate-400">Lead</span>
                        : <span className="rounded-full bg-teal-500/15 px-2.5 py-1 text-xs text-teal-300">Member</span>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data && data.pages > 1 && (
        <div className="mt-5 flex items-center justify-between">
          <p className="text-xs text-slate-500">Page {data.page} of {data.pages}</p>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-800 text-slate-300 hover:border-slate-600 disabled:opacity-30" data-testid="admin-users-prev"><CaretLeft size={16} /></button>
            <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-800 text-slate-300 hover:border-slate-600 disabled:opacity-30" data-testid="admin-users-next"><CaretRight size={16} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
